import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import SiteContactModal from './SiteContactModal';
import { siteContactService } from '../services/siteContactService';
import { customerService } from '../services/customerService';
import { siteService } from '../services/siteService';

const SiteContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [customers, setCustomers] = useState(null); // null = not yet loaded
  const [sites, setSites] = useState(null);         // null = not yet loaded
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const sortedContacts = [...contacts].sort((a, b) => {
    const aName = `${a.last_name} ${a.first_name}`.toLowerCase();
    const bName = `${b.last_name} ${b.first_name}`.toLowerCase();
    return sortDir === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
  });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await siteContactService.getSiteContacts();
      setContacts(data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load site contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    customerService.getCustomers().then(res => setCustomers(res.data || [])).catch(() => setCustomers([]));
    siteService.getSites().then(res => setSites(res.data || [])).catch(() => setSites([]));
  }, [fetchContacts]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddContact = async (formData) => {
    await siteContactService.createSiteContact(formData);
    await fetchContacts();
    setShowModal(false);
    showSuccess('Site contact added successfully!');
  };

  const handleEditContact = async (formData) => {
    await siteContactService.updateSiteContact(editingContact.id, formData);
    await fetchContacts();
    setEditingContact(null);
    setShowModal(false);
    showSuccess('Site contact updated successfully!');
  };

  const handleDeleteContact = async (id) => {
    try {
      await siteContactService.deleteSiteContact(id);
      await fetchContacts();
      setDeleteConfirm(null);
      showSuccess('Site contact deleted successfully!');
    } catch (err) {
      setError('Failed to delete site contact. Please try again.');
    }
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const [noPrereqTarget, setNoPrereqTarget] = useState(null); // 'customer' | 'site'

  const openAddModal = () => {
    if (customers !== null && customers.length === 0) {
      setNoPrereqTarget('customer');
      return;
    }
    if (sites !== null && sites.length === 0) {
      setNoPrereqTarget('site');
      return;
    }
    setEditingContact(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Site Contacts</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">

        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* No prerequisite prompt */}
        {noPrereqTarget === 'customer' && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">No customers found</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                You need at least one customer (and a site) before adding site contacts. Create a customer first.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setNoPrereqTarget(null)}
                className="px-3 py-2 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded-lg font-semibold text-sm hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => navigate('/customers')}
                className="whitespace-nowrap px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm"
              >
                Create Customer →
              </button>
            </div>
          </div>
        )}

        {noPrereqTarget === 'site' && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">No sites found</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                You need at least one site before adding site contacts. Create a site first.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setNoPrereqTarget(null)}
                className="px-3 py-2 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded-lg font-semibold text-sm hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => navigate('/sites')}
                className="whitespace-nowrap px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm"
              >
                Create Site →
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Site Contact List</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {contacts.length} {contacts.length === 1 ? 'site contact' : 'site contacts'} total
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
          >
            <span className="text-lg leading-none">+</span>
            Add Contact
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading site contacts...
              </div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">👤</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No site contacts yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Add your first site contact to get started.</p>
              <button
                onClick={openAddModal}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <span className="text-lg leading-none">+</span>
                Add Contact
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>Name {sortDir === 'asc' ? '↑' : '↓'}</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Site</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Position</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedContacts.map((contact) => (
                      <tr key={contact.id} className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                          {contact.first_name} {contact.last_name}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{contact.site?.name || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{contact.position || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{contact.email || '—'}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{contact.phone || '—'}</td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditModal(contact)}
                              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(contact)}
                              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {contacts.map((contact) => (
                  <div key={contact.id} className="p-5 space-y-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {contact.first_name} {contact.last_name}
                      </h3>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="text-sm text-blue-600 dark:text-blue-400 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(contact)}
                          className="text-sm text-red-600 dark:text-red-400 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {contact.site && <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{contact.site.name}</p>}
                    {contact.position && <p className="text-sm text-gray-500 dark:text-gray-400">{contact.position}</p>}
                    {contact.email && <p className="text-sm text-gray-600 dark:text-gray-300">{contact.email}</p>}
                    {contact.phone && <p className="text-sm text-gray-500 dark:text-gray-400">{contact.phone}</p>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {showModal && (
        <SiteContactModal
          contact={editingContact}
          onSave={editingContact ? handleEditContact : handleAddContact}
          onClose={closeModal}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Site Contact</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteContact(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteContacts;
